import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getUser, refreshMe } from "@/lib/userAuth";
import { Check, Loader2, ArrowRight, ArrowLeft, CalendarCheck, Clock, User, Lock, LogIn, UserPlus, Phone } from "lucide-react";

const ITALIAN_MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const ITALIAN_DAYS = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];

function formatDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Login gate shown when unauthenticated user reaches /prenota
function LoginGate({ preservedService }) {
  const nav = useNavigate();
  const returnPath = `/prenota${preservedService ? `?service=${preservedService}` : ""}`;
  const goToAuth = (mode) => {
    // Preserve the intended booking service so we can resume after login
    sessionStorage.setItem("labor_pending_booking", JSON.stringify({ service: preservedService, returnPath }));
    nav(mode === "register" ? "/registrati" : "/accedi");
  };
  return (
    <div className="max-w-xl mx-auto text-center py-8" data-testid="booking-login-gate">
      <div className="w-20 h-20 bg-labor-terracotta/10 text-labor-terracotta mx-auto flex items-center justify-center mb-6 rounded-full">
        <Lock className="w-10 h-10" />
      </div>
      <h1 className="font-display text-4xl mb-3">Accedi per prenotare</h1>
      <p className="text-labor-muted text-lg mb-2">
        Per prenotare un appuntamento devi prima registrarti o accedere al tuo account.
      </p>
      <p className="text-sm text-labor-muted mb-8">
        La registrazione è gratuita e ti dà anche 20 domande gratuite a Labor AI.
      </p>
      <div className="flex flex-wrap gap-3 justify-center">
        <button data-testid="gate-register" onClick={() => goToAuth("register")} className="olive-btn px-7 py-3.5 font-semibold flex items-center gap-2">
          <UserPlus className="w-5 h-5" /> Registrati
        </button>
        <button data-testid="gate-login" onClick={() => goToAuth("login")} className="border-2 border-labor-olive text-labor-olive hover:bg-labor-olive hover:text-white px-7 py-3.5 font-semibold flex items-center gap-2">
          <LogIn className="w-5 h-5" /> Accedi
        </button>
      </div>
      <p className="text-xs text-labor-muted mt-8">
        Torna alla <Link to="/" className="underline">home</Link> se hai sbagliato pagina.
      </p>
    </div>
  );
}

// Prompt shown when authenticated user has no phone number yet
function PhoneRequiredPrompt({ user, onDone }) {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!phone || phone.replace(/\s/g, "").length < 6) { setError("Inserisci un numero di telefono valido"); return; }
    setLoading(true); setError(null);
    try {
      await api.patch("/users/me", { phone });
      await refreshMe();
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || "Errore nel salvataggio");
    } finally { setLoading(false); }
  };
  return (
    <div className="max-w-xl mx-auto py-8" data-testid="phone-required">
      <div className="paper-card p-8">
        <div className="flex items-center gap-3 mb-4">
          <Phone className="w-10 h-10 text-labor-terracotta" />
          <div>
            <h2 className="font-display text-2xl">Aggiungi il tuo telefono</h2>
            <p className="text-labor-muted text-sm">Ciao {user.first_name}! Ci serve un numero di telefono per contattarti in caso di problemi con l'appuntamento.</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3 mt-6">
          <label className="text-sm font-medium">Numero di telefono *</label>
          <input
            data-testid="phone-input"
            type="tel"
            autoFocus
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="es. 333 1234567"
            className="w-full px-3 py-3 border border-labor-border focus:outline-none focus:ring-2 focus:ring-labor-terracotta text-lg"
          />
          {error && <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200">{error}</div>}
          <button data-testid="phone-submit" disabled={loading} type="submit" className="olive-btn w-full py-3.5 font-semibold flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Salva e continua</>}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Booking() {
  const [search] = useSearchParams();
  const [user, setUser] = useState(getUser());
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [waitMsg, setWaitMsg] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ is_tesserato: false, notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState(null);
  const [monthOffset, setMonthOffset] = useState(0);
  const [noShowWarn, setNoShowWarn] = useState(null);

  // Refresh auth state on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      const u = await refreshMe();
      if (!mounted) return;
      setUser(u);
      setCheckingAuth(false);
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/services").then((r) => {
      setServices(r.data.services);
      // Restore pending booking after login
      let pending = null;
      try { pending = JSON.parse(sessionStorage.getItem("labor_pending_booking") || "null"); } catch {}
      const q = search.get("service") || pending?.service;
      if (q) {
        const found = r.data.services.find((s) => s.id === q);
        if (found) { setSelectedService(found); setStep(2); }
      }
      if (pending) sessionStorage.removeItem("labor_pending_booking");
    });
    api.get("/staff").then((r) => setStaff(r.data.staff));
  }, [user, search]);

  const availableStaff = useMemo(() => {
    if (!selectedService) return [];
    return staff.filter((s) => s.services.includes(selectedService.id));
  }, [selectedService, staff]);

  useEffect(() => {
    if (step === 3 && selectedService && selectedStaff && selectedDate) {
      setLoadingSlots(true);
      setWaitMsg(null);
      api.get("/availability", {
        params: { service_id: selectedService.id, staff_id: selectedStaff.id, day: formatDate(selectedDate) }
      }).then((r) => { setAvailableSlots(r.data.slots || []); setBookedSlots(r.data.booked || []); })
        .finally(() => setLoadingSlots(false));
    }
  }, [step, selectedService, selectedStaff, selectedDate]);

  const joinWaitlist = async (t) => {
    if (!window.confirm(`L'orario ${t} è già occupato. Vuoi entrare in lista d'attesa? Se si libera, il posto verrà assegnato automaticamente al primo in coda e riceverai una notifica.`)) return;
    try {
      const r = await api.post("/waitlist", { service_id: selectedService.id, staff_id: selectedStaff.id, slot_date: formatDate(selectedDate), slot_time: t });
      setWaitMsg({ ok: true, text: `✓ Sei in lista d'attesa per le ${t} (posizione ${r.data.position}). Ti avviseremo con una notifica se il posto si libera.` });
    } catch (e) { setWaitMsg({ ok: false, text: e.response?.data?.detail || "Errore iscrizione lista d'attesa" }); }
  };

  // Check no-show history for the authenticated user (debounced)
  useEffect(() => {
    if (step !== 4 || !user?.email) { setNoShowWarn(null); return; }
    const t = setTimeout(() => {
      api.get("/no-show-check", { params: { email: user.email } })
        .then((r) => setNoShowWarn(r.data))
        .catch(() => setNoShowWarn(null));
    }, 300);
    return () => clearTimeout(t);
  }, [user, step]);

  // Calendar generation
  const calendarDays = useMemo(() => {
    const today = new Date();
    const base = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    const daysInMonth = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    const firstDay = (base.getDay() + 6) % 7; // Monday=0
    const arr = [];
    for (let i = 0; i < firstDay; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dt = new Date(base.getFullYear(), base.getMonth(), d);
      const dow = dt.getDay() === 0 ? 7 : dt.getDay();
      const isPast = dt < new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const serviceOk = !selectedService || selectedService.days.includes(dow);
      arr.push({ date: dt, disabled: isPast || !serviceOk });
    }
    return { days: arr, label: `${ITALIAN_MONTHS[base.getMonth()]} ${base.getFullYear()}` };
  }, [monthOffset, selectedService]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post("/bookings", {
        service_id: selectedService.id,
        staff_id: selectedStaff.id,
        slot_date: formatDate(selectedDate),
        slot_time: selectedTime,
        // Identity is enforced backend-side from the authenticated user (anti-spoofing).
        // We still send required fields for validation, but backend uses profile data.
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        phone: user?.phone || "",
        email: user?.email || "",
        is_tesserato: form.is_tesserato,
        notes: form.notes,
      });
      setConfirmation(res.data.booking);
      setStep(5);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore durante la prenotazione. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed3 = selectedDate && selectedTime;
  const canProceed4 = !selectedService?.tesserati_only || form.is_tesserato;

  // AUTH GATES
  if (checkingAuth) {
    return (
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-labor-olive" />
      </div>
    );
  }
  if (!user) {
    return (
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <LoginGate preservedService={search.get("service")} />
      </div>
    );
  }
  if (!user.phone) {
    return (
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <PhoneRequiredPrompt user={user} onDone={async () => { const u = await refreshMe(); setUser(u); }} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
      <div className="text-xs uppercase tracking-widest text-labor-muted">Prenotazione appuntamento</div>
      <h1 className="font-display text-4xl md:text-5xl mt-2 mb-2">Prenota il tuo appuntamento</h1>
      <p className="text-labor-muted mb-10">Pochi passi. Riceverai la conferma via email.</p>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-10 flex-wrap">
        {["Servizio","Operatore","Data e ora","Dati","Conferma"].map((label, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`w-9 h-9 flex items-center justify-center text-sm font-bold ${step > i+1 ? "bg-labor-olive text-white" : step === i+1 ? "bg-labor-terracotta text-white" : "bg-labor-border text-labor-muted"}`}>{i+1}</div>
            <div className={`text-sm hidden sm:block ${step === i+1 ? "text-labor-ink font-semibold" : "text-labor-muted"}`}>{label}</div>
            {i < 4 && <div className="w-6 h-px bg-labor-border hidden sm:block" />}
          </div>
        ))}
      </div>

      {/* STEP 1: SERVICE */}
      {step === 1 && (
        <div className="grid md:grid-cols-2 gap-4 animate-fade-in">
          {services.map((s) => (
            <button
              key={s.id}
              data-testid={`booking-service-${s.id}`}
              onClick={() => { setSelectedService(s); setSelectedStaff(null); setStep(2); }}
              className="paper-card p-6 text-left hover:border-b-labor-terracotta transition-all"
            >
              <div className="text-xs uppercase tracking-widest text-labor-muted mb-1">{s.category}</div>
              <div className="font-display text-xl">{s.name}</div>
              <div className="text-sm text-labor-muted mt-1">Durata: {s.duration} min {s.tesserati_only && "· Solo tesserati"}</div>
            </button>
          ))}
        </div>
      )}

      {/* STEP 2: STAFF */}
      {step === 2 && selectedService && (
        <div className="animate-fade-in">
          <div className="paper-card p-5 mb-6 bg-labor-bg">
            <div className="text-xs uppercase tracking-widest text-labor-muted">Servizio selezionato</div>
            <div className="font-display text-2xl">{selectedService.name}</div>
          </div>
          <h2 className="font-display text-2xl mb-4">Con chi vuoi prendere appuntamento?</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {availableStaff.map((p) => (
              <button
                key={p.id}
                data-testid={`booking-staff-${p.id}`}
                onClick={() => { setSelectedStaff(p); setStep(3); }}
                className="paper-card p-6 text-left hover:border-b-labor-terracotta"
              >
                <User className="w-8 h-8 text-labor-olive mb-3" />
                <div className="font-display text-xl">{p.name}</div>
                <div className="text-sm text-labor-muted mt-1">{p.role}</div>
              </button>
            ))}
          </div>
          <button onClick={() => setStep(1)} data-testid="booking-back-1" className="mt-6 text-labor-muted hover:text-labor-ink flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Indietro</button>
        </div>
      )}

      {/* STEP 3: DATE + TIME */}
      {step === 3 && selectedService && selectedStaff && (
        <div className="animate-fade-in grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="font-display text-2xl mb-4">Scegli la data</h2>
            <div className="paper-card p-5">
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setMonthOffset((o) => Math.max(0, o - 1))} disabled={monthOffset === 0} className="text-labor-olive disabled:text-labor-border">←</button>
                <div className="font-display text-lg">{calendarDays.label}</div>
                <button onClick={() => setMonthOffset((o) => Math.min(3, o + 1))} className="text-labor-olive">→</button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-xs uppercase text-labor-muted mb-2">
                {ITALIAN_DAYS.map((d) => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.days.map((c, i) => (
                  <button
                    key={i}
                    disabled={!c || c.disabled}
                    data-testid={c ? `booking-day-${formatDate(c.date)}` : undefined}
                    onClick={() => { setSelectedDate(c.date); setSelectedTime(null); }}
                    className={`aspect-square text-sm font-medium ${
                      !c ? "" :
                      c.disabled ? "text-labor-border cursor-not-allowed" :
                      selectedDate && formatDate(selectedDate) === formatDate(c.date) ? "bg-labor-terracotta text-white" :
                      "hover:bg-labor-olive hover:text-white text-labor-ink"
                    }`}
                  >
                    {c ? c.date.getDate() : ""}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <h2 className="font-display text-2xl mb-4">Scegli l'orario</h2>
            {!selectedDate && <div className="text-labor-muted">Seleziona prima una data.</div>}
            {selectedDate && loadingSlots && <Loader2 className="w-6 h-6 animate-spin text-labor-olive" />}
            {selectedDate && !loadingSlots && availableSlots.length === 0 && (
              <div className="paper-card p-5 text-labor-muted">Nessuno slot libero in questa data. {bookedSlots.length > 0 ? "Puoi metterti in lista d'attesa su un orario occupato qui sotto." : "Prova un altro giorno."}</div>
            )}
            {selectedDate && !loadingSlots && availableSlots.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {availableSlots.map((t) => (
                  <button
                    key={t}
                    data-testid={`booking-slot-${t}`}
                    onClick={() => setSelectedTime(t)}
                    className={`py-3 text-center border font-medium ${
                      selectedTime === t
                        ? "bg-labor-olive text-white border-labor-olive"
                        : "bg-white border-labor-border hover:border-labor-olive"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
            {selectedDate && !loadingSlots && bookedSlots.length > 0 && (
              <div className="mt-5" data-testid="booking-waitlist-section">
                <div className="text-sm font-semibold mb-1">Orari occupati · Lista d'attesa "Posto liberato"</div>
                <div className="text-xs text-labor-muted mb-2">Tocca un orario occupato per metterti in coda: se si libera, è tuo in automatico.</div>
                <div className="grid grid-cols-3 gap-2">
                  {bookedSlots.map((t) => (
                    <button key={t} data-testid={`booking-waitlist-slot-${t}`} onClick={() => joinWaitlist(t)}
                      className="py-2.5 text-center border border-dashed border-labor-border text-labor-muted line-through decoration-labor-terracotta hover:no-underline hover:border-labor-terracotta hover:text-labor-terracotta text-sm transition-colors">
                      {t}
                    </button>
                  ))}
                </div>
                {waitMsg && <div data-testid="booking-waitlist-msg" className={`mt-3 text-sm p-3 border ${waitMsg.ok ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-700"}`}>{waitMsg.text}</div>}
              </div>
            )}
          </div>
          <div className="md:col-span-2 flex justify-between mt-4">
            <button onClick={() => setStep(2)} className="text-labor-muted hover:text-labor-ink flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Indietro</button>
            <button disabled={!canProceed3} onClick={() => setStep(4)} data-testid="booking-next-4" className="olive-btn px-6 py-3 disabled:opacity-40 flex items-center gap-2 font-semibold">
              Prosegui <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* STEP 4: FORM */}
      {step === 4 && (
        <div className="animate-fade-in max-w-2xl">
          <h2 className="font-display text-2xl mb-2">Conferma i tuoi dati</h2>
          <p className="text-labor-muted text-sm mb-6">Questi sono i dati del tuo account che useremo per l'appuntamento. Se vuoi cambiarli, aggiorna il tuo <Link to="/profilo" className="underline text-labor-olive">profilo</Link>.</p>

          <div className="paper-card p-5 bg-labor-skyBg" data-testid="booking-user-summary">
            <div className="grid sm:grid-cols-2 gap-3 text-[15px]">
              <div><span className="text-labor-muted">Nome:</span> <strong>{user.first_name} {user.last_name}</strong></div>
              <div><span className="text-labor-muted">Email:</span> <strong>{user.email}</strong></div>
              <div><span className="text-labor-muted">Telefono:</span> <strong>{user.phone}</strong></div>
            </div>
          </div>

          {/* PROMEMORIA DOCUMENTI */}
          {selectedService?.docs?.length > 0 && (
            <div className="paper-card p-5 mt-4 border-l-4 border-l-labor-terracotta bg-orange-50" data-testid="docs-reminder">
              <div className="font-display text-lg mb-2 text-labor-terracotta">📋 Documenti da portare</div>
              <p className="text-xs text-labor-muted mb-3">Per <strong>{selectedService.name}</strong> ricordati di portare in ufficio:</p>
              <ul className="space-y-1 text-sm">
                {selectedService.docs.map((d, i) => <li key={i} className="flex gap-2"><span className="text-labor-terracotta">✓</span> {d}</li>)}
              </ul>
              <div className="mt-3 text-xs text-labor-muted italic">💡 Consiglio: se hai già SPID o CIE, portali. Velocizzano molto le pratiche telematiche.</div>
            </div>
          )}

          <div className="mt-6 space-y-4">
            {noShowWarn && noShowWarn.no_show_count > 0 && (
              <div className="paper-card p-4 border-l-4 border-l-labor-terracotta bg-red-50" data-testid="no-show-warning">
                <div className="flex items-start gap-3">
                  <div className="text-labor-terracotta text-2xl">⚠️</div>
                  <div className="text-sm">
                    <div className="font-semibold text-labor-terracotta mb-1">
                      Attenzione: risultano <strong>{noShowWarn.no_show_count}</strong> appuntament{noShowWarn.no_show_count === 1 ? "o" : "i"} non onorato{noShowWarn.no_show_count === 1 ? "" : "i"} con questa email.
                    </div>
                    <div className="text-labor-ink">
                      La preghiamo di presentarsi puntualmente o di avvisarci con almeno 24 ore di anticipo in caso di impossibilità.
                      Assenze ripetute possono comportare limitazioni nelle prenotazioni future.
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div>
              <label className="flex items-center gap-2 text-[15px]">
                <input data-testid="booking-tesserato" type="checkbox" checked={form.is_tesserato} onChange={(e) => setForm({...form, is_tesserato: e.target.checked})} className="w-5 h-5" />
                Sono già tesserato Labor
              </label>
              {selectedService?.tesserati_only && !form.is_tesserato && (
                <div className="text-labor-terracotta text-sm mt-1">Questo servizio è riservato ai tesserati.</div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Note (opzionale)</label>
              <textarea data-testid="booking-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3} className="w-full mt-1 px-3 py-3 border border-labor-border focus:outline-none focus:ring-2 focus:ring-labor-terracotta" />
            </div>
          </div>

          <div className="paper-card p-5 mt-6 bg-labor-bg">
            <div className="text-xs uppercase tracking-widest text-labor-muted mb-2">Riepilogo</div>
            <div className="grid sm:grid-cols-2 gap-2 text-[15px]">
              <div><strong>Servizio:</strong> {selectedService?.name}</div>
              <div><strong>Operatore:</strong> {selectedStaff?.name}</div>
              <div><strong>Data:</strong> {selectedDate?.toLocaleDateString("it-IT")}</div>
              <div><strong>Ora:</strong> {selectedTime}</div>
            </div>
          </div>

          {error && <div className="mt-4 paper-card p-3 border-l-4 border-l-labor-terracotta bg-red-50 text-red-800 text-sm">{error}</div>}

          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(3)} className="text-labor-muted hover:text-labor-ink flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Indietro</button>
            <button disabled={!canProceed4 || submitting} onClick={submit} data-testid="booking-submit" className="olive-btn px-6 py-3 disabled:opacity-40 flex items-center gap-2 font-semibold">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Prenoto…</> : <><CalendarCheck className="w-4 h-4" /> Conferma prenotazione</>}
            </button>
          </div>
        </div>
      )}

      {/* STEP 5: DONE */}
      {step === 5 && confirmation && (
        <div className="animate-fade-in text-center max-w-xl mx-auto" data-testid="booking-success">
          <div className="w-20 h-20 bg-labor-olive text-white mx-auto flex items-center justify-center mb-6">
            <Check className="w-10 h-10" />
          </div>
          <h2 className="font-display text-4xl mb-3">Prenotazione confermata!</h2>
          <p className="text-labor-muted mb-6">Riceverai una notifica sul telefono il giorno prima e 2 ore prima dell'appuntamento (attiva le notifiche dal tuo profilo). Ti aspettiamo in sede.</p>
          <div className="paper-card p-6 text-left">
            <div className="grid grid-cols-2 gap-3 text-[15px]">
              <div className="text-labor-muted">Servizio</div><div className="font-medium">{confirmation.service_name}</div>
              <div className="text-labor-muted">Operatore</div><div className="font-medium">{confirmation.staff_name}</div>
              <div className="text-labor-muted">Data</div><div className="font-medium">{confirmation.slot_date}</div>
              <div className="text-labor-muted">Ora</div><div className="font-medium">{confirmation.slot_time}</div>
              <div className="text-labor-muted">Codice</div><div className="font-mono text-xs">{confirmation.id}</div>
            </div>
          </div>
          <button onClick={() => { setStep(1); setSelectedService(null); setSelectedStaff(null); setSelectedDate(null); setSelectedTime(null); setForm({ is_tesserato:false,notes:"" }); setConfirmation(null); }} className="mt-6 text-labor-olive font-semibold hover:text-labor-terracotta">Prenota un altro appuntamento →</button>
        </div>
      )}
    </div>
  );
}
